/**
 * CsvExportUtil — single source of truth for CSV download row caps.
 *
 * WHY THIS EXISTS: every CSV export in the app is built as one in-memory string and
 * returned in the Lambda proxy response `body`. API Gateway caps that response at ~6 MB,
 * so an oversized CSV fails at the gateway (500/502) regardless of Lambda memory/timeout.
 * We keep exports under that wall with a per-export ROW cap rather than a byte guard,
 * because only Activity Notes have wide/variable rows (free-text body + multi-value
 * provider/contact/location/tag columns); every other export is narrow and predictable.
 *
 * Caps (validated against real staging/prod data; see csv-download-dialog-design.md):
 *   - Narrow exports (providers ~160 B/row, claims ~300 B/row): 1000 rows -> ~0.3 MB.
 *     Measured under 25% of the wall even unbounded, so lots of headroom (kept at 1000
 *     for now but could safely go much higher).
 *   - Activity Notes (~2.4 KB/note measured on the heaviest client, 2093 notes = 4.58 MB):
 *     1000 rows -> ~2.3 MB (38%). 2000 was measured at 76.6% (too close), so 1000 is the
 *     ceiling and leaves room for clients writing longer notes.
 *
 * Both the frontend dialog and the backend export paths import this, so the number the
 * user is offered and the number the server will actually honor can never drift apart.
 */
class CsvExportUtil {
  // Default cap for narrow, predictable exports. Set to 1000 for now; kept as its own
  // value (separate from the Activity Notes cap below) so the two can diverge later —
  // the data shows narrow exports could safely go much higher if we ever want.
  static get DEFAULT_ROW_CAP() {
    return 1000;
  }

  // Per-export overrides, keyed by the model name the emission point uses.
  // `ProviderComments` is the Activity Notes model (that IS the table name; "Activity
  // Notes" is just the UI label). Its rows are wide/variable (free-text body + multi-value
  // provider/contact/location/tag columns), so it gets a lower cap than the narrow default.
  // Kept as its own entry (rather than merged into DEFAULT_ROW_CAP) so it can be tuned
  // independently even though both are 1000 today.
  static get ROW_CAPS() {
    return {
      ProviderComments: 1000,
    };
  }

  /**
   * The row cap for a given export type (model or handler name). Falls back to the
   * default cap for anything not explicitly listed.
   * @param {string} exportType
   * @returns {number}
   */
  static getRowCap(exportType) {
    const caps = CsvExportUtil.ROW_CAPS;
    if (exportType && Object.prototype.hasOwnProperty.call(caps, exportType)) {
      return caps[exportType];
    }
    return CsvExportUtil.DEFAULT_ROW_CAP;
  }

  /**
   * Clamp a requested row limit to the cap for the export type. A missing/invalid/
   * non-positive limit resolves to the cap itself (so an unbounded request becomes the
   * cap rather than "everything").
   * @param {number|string} limit
   * @param {string} exportType
   * @returns {number}
   */
  static clampLimit(limit, exportType) {
    const cap = CsvExportUtil.getRowCap(exportType);
    const n = parseInt(limit, 10);
    if (!Number.isFinite(n) || n <= 0) {
      return cap;
    }
    return Math.min(n, cap);
  }
}

// CommonJS export: this util is consumed by BOTH the webpack frontend (named import via the
// utils barrel) and the plain-Node Lambda backend, so it must use module.exports — matching
// EncounterTypeUtil / StateUtil, which are imported the same way and work.
module.exports = { CsvExportUtil };
